import { motion } from "framer-motion";
import { CalendarCheck, Clock3, MapPin, UserCheck, Users } from "lucide-react";

import { Card, CardHeader, Row } from "@/components/Card";
import StatCard from "@/components/StatCard";
import { useApp } from "@/App";
import { students, type Role } from "@/data/mock";

const ROLE_COPY: Record<Role, { title: string; subtitle: string }> = {
  director: { title: "Attendance command", subtitle: "Spot patterns early and keep every class accounted for." },
  bursar: { title: "Attendance pulse", subtitle: "A quick operational view of who is present today." },
  teacher: { title: "Class attendance", subtitle: "Take a fast roll call and follow up on absences." },
  student: { title: "My attendance", subtitle: "Your presence record for Term 2, 2026." },
  parent: { title: "Family attendance", subtitle: "Keep track of attendance across your children." },
};

const ATTENDANCE_ROWS = [
  { title: "S3 Blue", subtitle: "Mathematics · Room 12", value: "96%", tone: "green" as const },
  { title: "S2 Green", subtitle: "Mathematics · Room 08", value: "92%", tone: "blue" as const },
  { title: "S4 Red", subtitle: "Physics · Lab 1", value: "88%", tone: "orange" as const },
  { title: "S1 Yellow", subtitle: "English · Room 04", value: "84%", tone: "red" as const },
];

const ROLE_ATTENDANCE_ROWS: Record<Role, typeof ATTENDANCE_ROWS> = {
  director: ATTENDANCE_ROWS,
  bursar: [
    { title: "S3 Blue", subtitle: "Fee follow-up list · 2 absences", value: "96%", tone: "green" },
    { title: "S4 Red", subtitle: "Fee follow-up list · 4 absences", value: "88%", tone: "orange" },
  ],
  teacher: [
    { title: "S3 Blue", subtitle: "Your Mathematics class · 32 students", value: "96%", tone: "green" },
    { title: "S2 Green", subtitle: "Your Mathematics class · 35 students", value: "92%", tone: "blue" },
  ],
  student: [
    { title: "Amina Nakato", subtitle: "Your attendance this term", value: "96%", tone: "green" },
    { title: "Mathematics", subtitle: "12 of 12 classes attended", value: "100%", tone: "blue" },
  ],
  parent: [
    { title: "Amina Nakato", subtitle: "S3 Blue · 2 absences", value: "96%", tone: "green" },
    { title: "Joel Wanyama", subtitle: "S1 Green · 4 absences", value: "89%", tone: "orange" },
  ],
};

export default function Attendance() {
  const role = useApp((s) => s.role) ?? "director";
  const copy = ROLE_COPY[role];
  const average = Math.round(students.reduce((sum, student) => sum + student.attendance, 0) / students.length);
  const attendanceRows = ROLE_ATTENDANCE_ROWS[role];
  const isPersonal = role === "student" || role === "parent";

  return (
    <div className="px-5 pb-8 pt-5">
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <p className="text-ios-footnote text-ios-secondary">Today · Wednesday, 19 February</p>
        <h1 className="mt-0.5 text-ios-title-1 font-bold text-ios-label">{copy.title}</h1>
        <p className="mt-1 max-w-sm text-ios-caption-1 text-ios-secondary">{copy.subtitle}</p>
      </motion.div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <StatCard label={isPersonal ? "My attendance" : "Present today"} value={isPersonal ? (role === "parent" ? "93%" : "96%") : "388"} caption={isPersonal ? "Term 2, 2026" : "Of 412 students"} tone="green" icon={UserCheck} spark={[361, 370, 374, 380, 381, 386, 388]} index={0} />
        <StatCard label={isPersonal ? "Classes attended" : "Attendance rate"} value={isPersonal ? "12 / 12" : `${average}%`} caption={isPersonal ? "This term" : "This term"} tone="brand" icon={CalendarCheck} spark={[88, 89, 90, 91, 91, 92, average]} index={1} />
        <StatCard label={isPersonal ? "Late arrivals" : "Late arrivals"} value={isPersonal ? "1" : "14"} caption={isPersonal ? "This term" : "Since first period"} tone="orange" icon={Clock3} index={2} />
        <StatCard label={isPersonal ? "Next check-in" : "Follow-ups"} value={isPersonal ? "Tomorrow" : "9"} caption={isPersonal ? "Mathematics" : "Need a guardian call"} tone="red" icon={Users} index={3} />
      </div>

      <Card className="mt-5" index={4}>
        <CardHeader title={isPersonal ? "My record" : "Attendance scope"} subtitle={isPersonal ? "Only records you are allowed to see" : "Attendance today"} />
        <div>
          {attendanceRows.map((item, index) => (
            <Row key={item.title} title={item.title} subtitle={item.subtitle} leading={MapPin} leadingTone={item.tone} trailing={item.value} chevron separator={index < ATTENDANCE_ROWS.length - 1} />
          ))}
        </div>
      </Card>

      {!isPersonal && <Card className="mt-5" index={5}>
        <CardHeader title="Needs attention" subtitle="Students below 80%" />
        <div>{students.filter((student) => student.attendance < 80).map((student, index, atRisk) => <Row key={student.id} title={student.name} subtitle={`${student.klass} · ${student.guardian}`} leading={Users} leadingTone="red" trailing={`${student.attendance}%`} separator={index < atRisk.length - 1} />)}</div>
      </Card>}
    </div>
  );
}
